import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { guias, trilhaGuias, TRILHA_NAME } from '../data/guias';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const AprendaPage: React.FC = () => {
  const categorias = Array.from(new Set(guias.map((g) => g.category)));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Aprenda Cripto | ${SITE_NAME}`,
    url: `${SITE_URL}/aprenda`,
    inLanguage: 'pt-BR',
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>Aprenda Cripto: Guias em Português para Iniciantes | {SITE_NAME}</title>
        <meta
          name="description"
          content="Trilha educativa gratuita de criptomoedas em português: o que é Bitcoin e blockchain, carteiras, segurança, como identificar golpes e como comprar de forma responsável."
        />
        <link rel="canonical" href={`${SITE_URL}/aprenda`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Aprenda</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Aprenda</h1>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl">
            Conteúdo educativo gratuito, em português e sem promessas de lucro: entenda os fundamentos,
            proteja-se de golpes e tome decisões mais conscientes. Comece pela trilha abaixo — na ordem — ou
            explore os guias por tema.
          </p>

          {/* Trilha */}
          <section className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-xl shadow-lg p-6 md:p-8 mb-10 text-white">
            <p className="text-blue-200 text-xs font-bold uppercase tracking-wider mb-1">🎓 Trilha educativa</p>
            <h2 className="text-2xl font-bold mb-1">{TRILHA_NAME}</h2>
            <p className="text-blue-100 text-sm mb-6">
              {trilhaGuias.length} lições em sequência, do zero à primeira compra responsável —
              aproximadamente {trilhaGuias.reduce((sum, g) => sum + g.minutes, 0)} minutos de leitura no total.
            </p>
            <ol className="space-y-2">
              {trilhaGuias.map((guide) => (
                <li key={guide.slug}>
                  <Link
                    to={`/aprenda/${guide.slug}`}
                    className="flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-3 transition"
                  >
                    <span className="flex-shrink-0 w-7 h-7 bg-yellow-400 text-blue-900 rounded-full flex items-center justify-center text-sm font-bold">
                      {guide.trilhaOrder}
                    </span>
                    <span className="font-semibold text-sm flex-grow">{guide.title}</span>
                    <span className="text-blue-200 text-xs flex-shrink-0">{guide.minutes} min</span>
                  </Link>
                </li>
              ))}
            </ol>
          </section>

          {/* Guias por categoria */}
          {categorias.map((categoria) => (
            <section key={categoria} className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{categoria}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {guias
                  .filter((guide) => guide.category === categoria)
                  .map((guide) => (
                    <Link
                      key={guide.slug}
                      to={`/aprenda/${guide.slug}`}
                      className="bg-white rounded-xl shadow p-5 hover:shadow-lg transition"
                    >
                      <h3 className="font-bold text-gray-900 mb-1">{guide.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-2">{guide.description}</p>
                      <p className="text-xs text-gray-400">{guide.minutes} min de leitura</p>
                    </Link>
                  ))}
              </div>
            </section>
          ))}

          {/* Glossário CTA */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-blue-900 mb-0.5">Glossário Cripto</p>
              <p className="text-blue-700 text-sm">
                25+ termos explicados em português, com exemplos práticos — o complemento ideal dos guias.
              </p>
            </div>
            <Link
              to="/glossario"
              className="flex-shrink-0 bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition"
            >
              Abrir Glossário
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
